import { Button } from '@/components/ui/Button';
import type { ReportBuilderResult } from './hooks/useReportBuilder';
import { formatReportCellValue } from './reportResultsUtils';

interface BuilderResultsTableProps {
  result: ReportBuilderResult;
  onExport?: () => void;
  showExport?: boolean;
  compact?: boolean;
}

export function BuilderResultsTable({
  result,
  onExport,
  showExport = false,
  compact = false,
}: BuilderResultsTableProps) {
  if (!result.columns.length) {
    return (
      <p className="py-8 text-center text-sm text-gray-500 dark:text-gray-400">
        No results found for the current configuration.
      </p>
    );
  }

  if (!result.rows.length) {
    return (
      <p className="py-8 text-center text-sm text-gray-500 dark:text-gray-400">
        No results found for the current configuration.
      </p>
    );
  }

  return (
    <div>
      {!compact && (
        <div className="mb-3 flex items-center justify-between">
          <p className="text-sm text-gray-500 dark:text-gray-400">
            {result.total.toLocaleString()} rows in {result.executionTimeMs}ms
          </p>
          {showExport && onExport && (
            <Button variant="outline" size="sm" onClick={onExport}>
              Export Excel
            </Button>
          )}
        </div>
      )}
      <div className="max-h-[min(60vh,520px)] overflow-auto rounded-lg border border-border dark:border-border-dark">
        <table className="w-full text-left text-sm" role="grid" aria-label="Report results">
          <thead className="sticky top-0 bg-gray-50 dark:bg-surface-dark-tertiary">
            <tr>
              {result.columns.map((col) => (
                <th
                  key={col.id}
                  className="whitespace-nowrap px-4 py-3 text-xs font-semibold uppercase tracking-wide text-gray-500 dark:text-gray-400"
                >
                  {col.label}
                </th>
              ))}
            </tr>
          </thead>
          <tbody className="divide-y divide-border dark:divide-border-dark">
            {result.rows.map((row, rowIndex) => (
              <tr
                key={rowIndex}
                className="transition-colors hover:bg-surface-secondary dark:hover:bg-surface-dark-tertiary"
              >
                {result.columns.map((col) => (
                  <td
                    key={col.id}
                    className="whitespace-nowrap px-4 py-2.5 text-gray-900 dark:text-gray-100"
                  >
                    {formatReportCellValue(row[col.id], col.format)}
                  </td>
                ))}
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </div>
  );
}
